import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import eventsApi from '../api/eventsApi';
import { CraysEvent, LoadingStatus } from '../../types';

interface EventsState {
  events: CraysEvent[];
  selectedEvent: CraysEvent | null;
  hasMore: boolean;
  cursor: string | null;
  status: LoadingStatus;
  error: string | null;
}

const initialState: EventsState = {
  events: [],
  selectedEvent: null,
  hasMore: false,
  cursor: null,
  status: 'idle',
  error: null,
};

export const fetchEvents = createAsyncThunk(
  'events/fetchAll',
  async (cursor: string | undefined, { rejectWithValue }) => {
    try {
      const res = await eventsApi.listEvents(cursor);
      return res as { data: { items: CraysEvent[]; meta: { cursor: string | null; hasMore: boolean } } };
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to load events');
    }
  },
);

export const fetchEvent = createAsyncThunk(
  'events/fetchOne',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await eventsApi.getEvent(id);
      return res.data as CraysEvent;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to load event');
    }
  },
);

export const rsvpEvent = createAsyncThunk(
  'events/rsvp',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await eventsApi.rsvpEvent(id);
      return res.data as CraysEvent;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to RSVP');
    }
  },
);

export const cancelRsvp = createAsyncThunk(
  'events/cancelRsvp',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await eventsApi.cancelRsvp(id);
      return res.data as CraysEvent;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to cancel RSVP');
    }
  },
);

const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    clearSelectedEvent(state) {
      state.selectedEvent = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvents.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchEvents.fulfilled, (state, { payload, meta }) => {
        const items = payload.data.items;
        state.events = meta.arg ? [...state.events, ...items] : items;
        state.cursor = payload.data.meta.cursor;
        state.hasMore = payload.data.meta.hasMore;
        state.status = 'succeeded';
      })
      .addCase(fetchEvents.rejected, (state, { payload }) => {
        state.status = 'failed';
        state.error = payload as string;
      })
      .addCase(fetchEvent.fulfilled, (state, { payload }) => {
        state.selectedEvent = payload;
      })
      .addCase(rsvpEvent.fulfilled, (state, { payload }) => {
        state.events = state.events.map((e) => e._id === payload._id ? payload : e);
        if (state.selectedEvent?._id === payload._id) state.selectedEvent = payload;
      })
      .addCase(cancelRsvp.fulfilled, (state, { payload }) => {
        state.events = state.events.map((e) => e._id === payload._id ? payload : e);
        if (state.selectedEvent?._id === payload._id) state.selectedEvent = payload;
      });
  },
});

export const { clearSelectedEvent } = eventsSlice.actions;
export default eventsSlice.reducer;

export const selectEvents = (state: { events: EventsState }) => state.events.events;
export const selectSelectedEvent = (state: { events: EventsState }) => state.events.selectedEvent;
export const selectEventsStatus = (state: { events: EventsState }) => state.events.status;
export const selectEventsHasMore = (state: { events: EventsState }) => state.events.hasMore;
export const selectEventsCursor = (state: { events: EventsState }) => state.events.cursor;
